import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, FormField } from "@components/input";
import { useLocale } from "@hooks/useLocale";
import { useAppDispatch } from "@app/hooks";
import { selectWell, setActiveEvent } from "@app/slices/wellContextSlice";
import { mApi, useGetWellsQuery } from "@api/mApi";
import { useImportWorkoverMutation } from "@api/pApi";
import { downloadPFile } from "@api/pDownload";
import type { ImportWorkoverResult } from "@api/types";

export interface ImportExcelModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Importar desde Excel — the admin-only modal that bulk-loads a workover (well
 * + event + daily reports + activities + cost/AFE lines + catalog entries) from
 * a filled template .xlsx.
 *
 * TWO-STEP flow (F4):
 *   1. "Descargar plantilla" — GET /v1/import/workover/template.xlsx via the
 *      authed `downloadPFile` helper (same bearer token as the RTK base query).
 *   2. File picker (.xlsx) -> immediately POST multipart to
 *      `/v1/import/workover?dry_run=true` (dry run). The backend parses +
 *      validates + computes the SAME expanded summary — per-section creates/
 *      updates, whether an existing event will MATCH or a new one is created,
 *      new catalog entries, and a `warnings[]` list — WITHOUT writing anything.
 *      We render that as a PREVIEW panel.
 *   3. "Confirmar importación" -> POST again WITHOUT dry_run (real write) ->
 *      show the final summary, invalidate caches, offer "Ir al parte diario".
 *      "Cancelar" backs out without writing.
 *
 * If the dry-run itself fails we surface the error and NEVER fall through to a
 * real write. A 422 (bad template) surfaces its Spanish detail inline, in both
 * the preview and confirm phases.
 */
export function ImportExcelModal({ open, onClose }: ImportExcelModalProps) {
  const { t } = useLocale();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { data: wells = [] } = useGetWellsQuery();
  const [importWorkover, { isLoading: busy }] = useImportWorkoverMutation();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  // The dry-run PREVIEW (nothing written yet) and, after confirm, the final
  // real-write summary. `preview` gates the "Confirmar importación" button.
  const [preview, setPreview] = useState<ImportWorkoverResult | null>(null);
  const [result, setResult] = useState<ImportWorkoverResult | null>(null);

  // Reset whenever it (re)opens so a prior run never leaks in.
  useEffect(() => {
    if (!open) return;
    setFile(null);
    setDownloading(false);
    setDownloadError(null);
    setImportError(null);
    setPreview(null);
    setResult(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [open]);

  const downloadTemplate = async () => {
    setDownloadError(null);
    setDownloading(true);
    try {
      await downloadPFile("/import/workover/template.xlsx", "plantilla_workover.xlsx");
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : t("import.err.download"));
    } finally {
      setDownloading(false);
    }
  };

  // Shared RTK error -> localized string. A 422 carries { data: { detail } }
  // with a Spanish message from the backend parser; otherwise a generic fallback.
  const toMsg = (err: unknown, fallbackKey: "import.err.preview" | "import.err.generic") => {
    const e = err as { data?: { detail?: unknown } };
    const detail = e?.data?.detail;
    return typeof detail === "string" && detail ? detail : t(fallbackKey);
  };

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setImportError(null);
    setPreview(null);
    setResult(null);
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    // Step 1: on select, run the DRY RUN immediately (no writes) -> preview.
    if (f) void runPreview(f);
  };

  // Step 1 — dry-run. Writes nothing. On failure we surface the error and do
  // NOT proceed to a real import (preview stays null, confirm stays disabled).
  const runPreview = async (f: File) => {
    setImportError(null);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const summary = await importWorkover({ formData: fd, dryRun: true }).unwrap();
      setPreview(summary);
    } catch (err) {
      setImportError(toMsg(err, "import.err.preview"));
    }
  };

  // Step 2 — the real write, only reachable from the preview via explicit
  // "Confirmar importación". Re-sends the same file WITHOUT dry_run.
  const confirmImport = async () => {
    if (!file) return;
    setImportError(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const summary = await importWorkover({ formData: fd }).unwrap();
      setResult(summary);
      setPreview(null);

      // The import created (or re-pointed) the well in M — invalidate the wells
      // LIST so the register refetches and we can resolve the new well's M id.
      dispatch(mApi.util.invalidateTags([{ type: "Well", id: "LIST" }]));
    } catch (err) {
      setImportError(toMsg(err, "import.err.generic"));
    }
  };

  // After a successful import, select the new well + event and go to the daily.
  const goToDaily = () => {
    if (!result) return;
    const w = wells.find((x) => x.uwi === result.well_uwi);
    dispatch(selectWell(w?.id ?? result.well_uwi));
    dispatch(setActiveEvent(result.event_id));
    onClose();
    navigate("/daily");
  };

  // Prefer the F1 split counts; fall back to the legacy single totals so an
  // older backend summary still renders a number.
  const costCreated = preview?.cost_lines_created ?? preview?.cost_lines ?? 0;
  const costUpdated = preview?.cost_lines_updated ?? 0;
  const afeCreated = preview?.afe_lines_created ?? preview?.afe_lines ?? 0;
  const afeUpdated = preview?.afe_lines_updated ?? 0;

  const sections: { label: string; created: number; updated: number }[] = preview
    ? [
        {
          label: t("import.sum.reports"),
          created: preview.reports_created,
          updated: preview.reports_updated ?? 0,
        },
        {
          label: t("import.sum.activities"),
          created: preview.activities_created,
          updated: preview.activities_updated ?? 0,
        },
        { label: t("import.sum.costLines"), created: costCreated, updated: costUpdated },
        { label: t("import.sum.afeLines"), created: afeCreated, updated: afeUpdated },
      ]
    : [];

  let footer: React.ReactNode;
  if (result) {
    footer = (
      <>
        <button type="button" className="btn" onClick={onClose}>
          {t("createwo.cancel")}
        </button>
        <button type="button" className="btn primary" onClick={goToDaily}>
          {t("import.goToDaily")}
        </button>
      </>
    );
  } else if (preview) {
    footer = (
      <>
        <button type="button" className="btn" onClick={onClose} disabled={busy}>
          {t("createwo.cancel")}
        </button>
        <button
          type="button"
          className="btn primary"
          onClick={() => void confirmImport()}
          disabled={busy}
        >
          {busy ? t("import.confirming") : t("import.confirm")}
        </button>
      </>
    );
  } else {
    footer = (
      <button type="button" className="btn" onClick={onClose} disabled={busy}>
        {t("createwo.cancel")}
      </button>
    );
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("import.title")}
      context={t("createwo.step.workover")}
      width={620}
      footer={footer}
    >
      <div className="mform">
        <FormField label={t("import.template")} owner="p">
          <div>
            <button
              type="button"
              className="btn"
              onClick={() => void downloadTemplate()}
              disabled={downloading}
            >
              {downloading ? t("import.downloading") : t("import.downloadTemplate")}
            </button>
            {downloadError && (
              <div className="err" style={{ marginTop: 6 }} role="alert">
                {downloadError}
              </div>
            )}
          </div>
        </FormField>

        {!result && (
          <FormField label={t("import.file")} owner="p">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              aria-label={t("import.file")}
              onChange={onPick}
              disabled={busy}
            />
            {busy && !preview && (
              <p className="subtitle" style={{ marginTop: 6 }}>
                {t("import.preview.loading")}
              </p>
            )}
          </FormField>
        )}

        {importError && (
          <div className="err" role="alert">
            {importError}
          </div>
        )}

        {/* Step 1 — PREVIEW (dry run; nothing written yet). */}
        {preview && !result && (
          <FormField label={t("import.preview.title")} owner="p">
            <table>
              <tbody>
                <tr>
                  <td>{t("import.sum.well")}</td>
                  <td className="mono">
                    {preview.well_uwi}
                    {preview.created_well ? ` - ${t("import.sum.createdWell")}` : ""}
                  </td>
                </tr>
                <tr>
                  <td>{t("import.preview.event")}</td>
                  <td>
                    {preview.matched_event
                      ? t("import.preview.matchedEvent")
                      : t("import.preview.newEvent")}
                  </td>
                </tr>
                {sections.map((sec) => (
                  <tr key={sec.label}>
                    <td>{sec.label}</td>
                    <td className="tab-nums">
                      {t("import.preview.creates", { n: sec.created })} /{" "}
                      {t("import.preview.updates", { n: sec.updated })}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td>{t("import.preview.newCatalogs")}</td>
                  <td className="tab-nums">{preview.catalogs_added}</td>
                </tr>
              </tbody>
            </table>

            <p className="subtitle" style={{ marginTop: 10, marginBottom: 4 }}>
              {t("import.preview.warnings")}
            </p>
            {preview.warnings && preview.warnings.length > 0 ? (
              <ul role="alert" style={{ margin: 0, paddingLeft: 18 }}>
                {preview.warnings.map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            ) : (
              <p className="subtitle">{t("import.preview.noWarnings")}</p>
            )}
          </FormField>
        )}

        {/* Step 2 — final summary after the real write. */}
        {result && (
          <FormField label={t("import.summary")} owner="p">
            <table>
              <tbody>
                <tr>
                  <td>{t("import.sum.well")}</td>
                  <td className="mono">
                    {result.well_uwi}
                    {result.created_well ? ` - ${t("import.sum.createdWell")}` : ""}
                  </td>
                </tr>
                <tr>
                  <td>{t("import.sum.event")}</td>
                  <td className="mono">{result.event_id}</td>
                </tr>
                <tr>
                  <td>{t("import.sum.reports")}</td>
                  <td className="tab-nums">{result.reports_created}</td>
                </tr>
                <tr>
                  <td>{t("import.sum.activities")}</td>
                  <td className="tab-nums">{result.activities_created}</td>
                </tr>
                <tr>
                  <td>{t("import.sum.costLines")}</td>
                  <td className="tab-nums">{result.cost_lines_created ?? result.cost_lines ?? 0}</td>
                </tr>
                <tr>
                  <td>{t("import.sum.afeLines")}</td>
                  <td className="tab-nums">{result.afe_lines_created ?? result.afe_lines ?? 0}</td>
                </tr>
                <tr>
                  <td>{t("import.sum.catalogs")}</td>
                  <td className="tab-nums">{result.catalogs_added}</td>
                </tr>
              </tbody>
            </table>
          </FormField>
        )}
      </div>
    </Modal>
  );
}
